import { absoluteUrl, getSiteOrigin } from "@/constants/site";
import { DEFAULT_OG_IMAGE_PATH, SEO_BRAND } from "@/constants/seoConfig";

export interface SEOArticleMeta {
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

export interface SEOMeta {
  title: string;
  description: string;
  /** Path only, e.g. `/blog` or `/blog/my-slug` — used for canonical & og:url */
  canonicalPath: string;
  keywords?: string;
  ogImagePath?: string;
  ogType?: "website" | "article";
  robots?: string;
  article?: SEOArticleMeta;
}

function setMeta(selectorAttr: "name" | "property", key: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[${selectorAttr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(selectorAttr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function removeMeta(selectorAttr: "name" | "property", key: string) {
  document.querySelector(`meta[${selectorAttr}="${key}"]`)?.remove();
}

function clearArticleMetaTags() {
  document.querySelectorAll('meta[property^="article:"]').forEach((n) => n.remove());
}

/**
 * Updates document title, meta tags, canonical link, and Open Graph / Twitter fields.
 */
export function updateSEOMeta(meta: SEOMeta) {
  if (typeof document === "undefined") return;

  const origin = getSiteOrigin();
  const path = meta.canonicalPath.startsWith("/") ? meta.canonicalPath : `/${meta.canonicalPath}`;
  const canonicalUrl = `${origin}${path}`;
  const ogImage = absoluteUrl(meta.ogImagePath || DEFAULT_OG_IMAGE_PATH);

  clearArticleMetaTags();

  document.title = meta.title;

  setMeta("name", "description", meta.description);
  if (meta.keywords) setMeta("name", "keywords", meta.keywords);
  else removeMeta("name", "keywords");

  setMeta(
    "name",
    "robots",
    meta.robots ?? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
  );

  setMeta("property", "og:title", meta.title);
  setMeta("property", "og:description", meta.description);
  setMeta("property", "og:type", meta.ogType ?? "website");
  setMeta("property", "og:url", canonicalUrl);
  setMeta("property", "og:image", ogImage);
  setMeta("property", "og:locale", "en_US");
  setMeta("property", "og:site_name", SEO_BRAND);

  if (meta.ogType === "article" && meta.article) {
    if (meta.article.publishedTime)
      setMeta("property", "article:published_time", meta.article.publishedTime);
    if (meta.article.modifiedTime)
      setMeta("property", "article:modified_time", meta.article.modifiedTime);
    if (meta.article.section) setMeta("property", "article:section", meta.article.section);
    meta.article.tags?.forEach((tag) => {
      const el = document.createElement("meta");
      el.setAttribute("property", "article:tag");
      el.content = tag;
      document.head.appendChild(el);
    });
  }

  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", meta.title);
  setMeta("name", "twitter:description", meta.description);
  setMeta("name", "twitter:image", ogImage);
  setMeta("name", "twitter:site", "@avachatai");

  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = canonicalUrl;
}

export function injectJsonLd(id: string, data: object) {
  if (typeof document === "undefined") return () => {};
  document.getElementById(id)?.remove();
  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
  return () => {
    document.getElementById(id)?.remove();
  };
}
